import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Address, toNano } from '@ton/core';
import { getToken } from '../lib/api';
import type { TokenResponse } from '@memepad/shared';
import {
  formatTon,
  parseTon,
  buildBuyBody,
  buildSellTransferBody,
  nextQueryId,
} from '../lib/ton';
import {
  nanoToTonDisplay,
  progressPct,
  quoteSellTonOut,
  maxBuyNano,
} from '../lib/displayMath';
import { getJettonWalletAddress } from '../lib/jettonWallet';
import { stonfiSwapUrl, okxTokenUrl } from '../lib/dex';
import { useAppStore } from '../store';
import { useTonWallet } from '../hooks/useTonWallet';
import { DexBadge } from '../components/DexBadge';

type Mode = 'buy' | 'sell';

// CC-040 — open an external DEX page. openLink is the Mini App SDK call
// for regular web URLs (app.ston.fi / web3.okx.com).
function openDexLink(url: string): void {
  const tg = window.Telegram?.WebApp;
  if (tg && typeof tg.openLink === 'function') {
    tg.openLink(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// CC-037 — sell-transfer TON budget. `forward_ton_amount` (0.15 TON)
// covers the curve's notification + its TON-return message with gas-chain
// headroom; the gas reserve covers the seller's own jetton wallet
// send/compute. Unused TON bounces back to the seller.
const SELL_FORWARD_TON = toNano('0.15');
const SELL_GAS_RESERVE = toNano('0.15');

/**
 * Token detail route — Buy/Sell UI for an in-progress bonding curve.
 *
 * CC-025 (rollback of the over-engineered version)
 * ─────────────────────────────────────────────────
 *   The previous iteration tried to mirror `bonding_curve.tact:receive(Buy)`
 *   off-chain — computing exact graduation amounts, clamp predictions,
 *   refund estimates, "Max safe buy" buttons. Two structural problems:
 *
 *     1. Two sources of truth. Off-chain math drifts from on-chain
 *        whenever a constant (virtualTon, fee_bps, gas reserve) is
 *        bumped, and the bug is silent until users complain about
 *        wrong-looking receipts. Today: the bot's /api/token/:addr
 *        hardcodes virtualTon=30 TON regardless of the actual
 *        per-deploy value (0.5 on dev), so every off-chain quote was
 *        structurally wrong on dev tokens.
 *
 *     2. Predicting the clamp/refund branch is redundant. The contract
 *        already does it correctly, and the user's wallet (Tonkeeper)
 *        shows them the exact TON-in / jettons-out / refund split in
 *        the pre-sign preview. We were reinventing a guard the wallet
 *        already provides.
 *
 *   The honest model (this version):
 *
 *     - User types any amount. We don't quote off-chain math.
 *     - Receipt just says: "Curve has Y TON remaining; any excess will
 *       be refunded automatically by the contract."
 *     - minOut = 0 — slippage protection is delegated to the wallet
 *       preview. (The 5-second TON block time + low-traffic memepad
 *       traffic profile makes mempool slippage a non-issue here; a
 *       future PR can add on-chain-observed slippage if needed.)
 *     - Buy button disables only on obvious-invalid input
 *       (≤ 0, > 1000 TON safety cap).
 *
 *   What we KEEP from the previous iteration:
 *
 *     - The progress bar fix (BigInt division was truncating sub-TON
 *       values to 0); routed through `lib/displayMath`.
 *     - The wallet-ask convention for the input field — what you type
 *       is what the wallet asks to send. The contract subtracts its
 *       0.05 TON gas reserve and any 1% fee internally; the wallet
 *       preview surfaces the full breakdown.
 */
export function TokenRoute() {
  const { masterAddr } = useParams<{ masterAddr: string }>();
  const me = useAppStore((s) => s.me);
  const [data, setData] = useState<TokenResponse | null>(null);
  const [mode, setMode] = useState<Mode>('buy');
  const [input, setInput] = useState('');
  // CC-037 — sell tab has its own input (jetton amount, not a TON amount).
  const [sellInput, setSellInput] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  // CC-037 — seller's jetton wallet for this token, derived once on-chain.
  const [userJettonWallet, setUserJettonWallet] = useState<Address | null>(null);
  const [jettonWalletError, setJettonWalletError] = useState(false);
  // Which mode submitted the in-flight tx — so the shared wallet-state
  // effect below shows a buy- vs sell-appropriate toast.
  const submittedModeRef = useRef<Mode>('buy');

  const wallet = useTonWallet();

  useEffect(() => {
    if (!masterAddr) return;
    getToken(masterAddr).then(setData).catch(console.error);
  }, [masterAddr]);

  // CC-037 — derive the seller's jetton wallet for this token's master
  // via the TEP-89 `get_wallet_address` get-method. Runs once per
  // (token, wallet) pair and is cached in state — not re-fetched on
  // every render. Only needed when selling is actually possible.
  useEffect(() => {
    setUserJettonWallet(null);
    setJettonWalletError(false);
    const t = data?.token;
    const owner = wallet.address;
    if (!t || !owner) return;
    if (!(t.dexStatus === 'idle' && !t.isGraduated)) return; // sellable only
    let cancelled = false;
    getJettonWalletAddress(Address.parse(t.masterAddr), Address.parse(owner))
      .then((addr) => {
        if (!cancelled) setUserJettonWallet(addr);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('jetton wallet derivation failed', err);
        setJettonWalletError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [data, wallet.address]);

  // Surface success/error from wallet state machine via toast.
  useEffect(() => {
    if (wallet.state.kind === 'success') {
      setToast(
        submittedModeRef.current === 'sell'
          ? 'Продажа отправлена. Жди подтверждения в кошельке.'
          : 'Покупка отправлена. Жди подтверждения в кошельке.',
      );
    } else if (wallet.state.kind === 'error') {
      setToast(`Ошибка отправки: ${wallet.state.message}`);
    }
  }, [wallet.state]);

  // After 5s of success/error, snap state machine back to ready and clear toast.
  useEffect(() => {
    if (wallet.state.kind !== 'success' && wallet.state.kind !== 'error') {
      return;
    }
    const t = setTimeout(() => {
      wallet.reset();
      setToast(null);
    }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.state.kind]);

  if (!data) {
    return <div className="container"><div className="skeleton" style={{ height: 120 }} /></div>;
  }

  const { token, curveState } = data;
  // CC-052-E — `curveState` is optional: it's omitted for cohort-2 legacy
  // tokens whose on-chain curve predates the cohort-4 get-methods. When
  // it's missing the token is view-only — no Buy/Sell, no live reserves.
  const isLegacy =
    data.cohortStatus === 'cohort_2_legacy' || curveState === undefined;
  // CC-025: same conversion helpers the Home feed cards use, so a curve
  // showing 9.9% on the feed reads 9.9% in the detail header too. Falls
  // back to the token row's `realTonReserves` when `curveState` is absent.
  const realNano = curveState
    ? BigInt(curveState.realTon as any)
    : BigInt(token.realTonReserves as any);
  const targetNano = BigInt(token.targetTon as any);
  const tonReserves = nanoToTonDisplay(realNano);
  const targetTon = nanoToTonDisplay(targetNano);
  const pct = progressPct(realNano, targetNano);
  const remainingNano = targetNano > realNano ? targetNano - realNano : 0n;

  // Parse what the user typed. We DON'T compute jetton-out / fees /
  // refund — that's the wallet preview's job. We just need to know
  // whether the typed value parses and falls in the safety range.
  const SAFETY_CAP_NANO = 1_000_000_000_000n; // 1000 TON — well above any sane buy
  let walletSendNano: bigint | null = null;
  let inputError: 'invalid' | 'zero' | 'tooLarge' | null = null;
  if (input.trim().length > 0) {
    try {
      const n = parseTon(input);
      if (n <= 0n) inputError = 'zero';
      else if (n > SAFETY_CAP_NANO) inputError = 'tooLarge';
      else walletSendNano = n;
    } catch {
      inputError = 'invalid';
    }
  }

  // CC-037 — selling is allowed only while the token is live on the
  // bonding curve. graduated / graduating / migrating / lp_* /
  // migration_failed all mean the curve is closed for sells.
  const sellable = token.dexStatus === 'idle' && !token.isGraduated;

  // CC-037 — parse the sell input (jetton amount). Jettons here are
  // 9-decimal (TON convention), so parseTon does the right base-1e9
  // parse. We don't quote the TON output off-chain — the wallet preview
  // shows the exact amounts, same trade-off the buy flow makes.
  let sellJettonNano: bigint | null = null;
  let sellInputError: 'invalid' | 'zero' | null = null;
  if (sellInput.trim().length > 0) {
    try {
      const n = parseTon(sellInput);
      if (n <= 0n) sellInputError = 'zero';
      else sellJettonNano = n;
    } catch {
      sellInputError = 'invalid';
    }
  }

  // CC-037 — estimated TON payout (constant-product + 1% fee, mirrors the
  // curve). Display-only "получишь примерно"; the wallet preview shows the
  // exact amount and minTonOut stays 0, so an approximate quote is fine.
  const sellQuoteNano =
    sellJettonNano !== null && curveState
      ? quoteSellTonOut({
          jettonIn: sellJettonNano,
          realTon: BigInt(curveState.realTon as any),
          realJetton: BigInt(curveState.realJetton as any),
          virtualTon: BigInt(curveState.virtualTon as any),
          virtualJetton: BigInt(curveState.virtualJetton as any),
        })
      : null;

  const onConfirm = async () => {
    setToast(null);

    // ── CC-037 — Sell: TEP-74 jetton transfer into the bonding curve ──
    if (mode === 'sell') {
      if (!sellable) {
        setToast('Продажа недоступна: токен больше не торгуется на bonding curve.');
        return;
      }
      if (sellJettonNano === null) {
        setToast('Введи количество токенов.');
        return;
      }
      if (wallet.state.kind !== 'ready') {
        setToast(`Кошелёк не готов (${wallet.state.kind})`);
        return;
      }
      if (!userJettonWallet) {
        setToast('Не удалось определить адрес твоего jetton-кошелька. Обнови страницу.');
        return;
      }
      submittedModeRef.current = 'sell';
      // minTonOut = 0 — slippage is delegated to the wallet's pre-sign
      // preview, mirroring the buy flow (see module comment / CC-025).
      const sellBody = buildSellTransferBody({
        queryId: nextQueryId(),
        jettonAmount: sellJettonNano,
        curveAddr: Address.parse(token.curveAddr),
        userAddr: Address.parse(wallet.state.address),
        minTonOut: 0n,
        forwardTonAmount: SELL_FORWARD_TON,
      });
      // The transfer goes to the SELLER's own jetton wallet; `value`
      // covers forward_ton_amount + that wallet's own send/compute gas.
      await wallet.signAndSend({
        to: userJettonWallet,
        value: SELL_FORWARD_TON + SELL_GAS_RESERVE,
        body: sellBody,
        bounce: true,
      });
      return;
    }

    if (walletSendNano === null) {
      setToast('Введи сумму.');
      return;
    }
    if (wallet.state.kind !== 'ready') {
      setToast(`Кошелёк не готов (${wallet.state.kind})`);
      return;
    }

    submittedModeRef.current = 'buy';
    // minOut = 0 — no off-chain slippage protection. The wallet preview
    // (Tonkeeper) already shows the user the exact jettons they will
    // receive before they sign; that IS the slippage guard. See the
    // module-level comment for the trade-off.
    const queryId = nextQueryId();
    const body = buildBuyBody({
      queryId,
      minOut: 0n,
      recipient: Address.parse(wallet.state.address),
    });

    // `value` = wallet ask = what the user typed. Contract subtracts
    // SWAP_GAS_RESERVE (0.05 TON) and 1% fee internally. Any excess
    // past `targetTon − realTon` is refunded automatically by the
    // contract's clamp branch.
    await wallet.signAndSend({
      to: Address.parse(token.curveAddr),
      value: walletSendNano,
      body,
      bounce: true,
    });
  };

  const isWorking =
    wallet.state.kind === 'signing' || wallet.state.kind === 'submitting';
  // CC-037 — disabled logic is mode-specific. Buy branch is unchanged.
  const buttonDisabled =
    mode === 'sell'
      ? wallet.state.kind !== 'ready' ||
        !sellable ||
        sellJettonNano === null ||
        userJettonWallet === null
      : wallet.state.kind !== 'ready' || walletSendNano === null;

  let buttonLabel: ReactNode;
  if (wallet.state.kind === 'signing') buttonLabel = 'Подписание…';
  else if (wallet.state.kind === 'submitting') buttonLabel = 'Отправка…';
  else buttonLabel = mode === 'buy' ? `Купить $${token.ticker}` : `Продать $${token.ticker}`;

  return (
    <div className="container vgap-16">
      <div className="row">
        <img
          src={`https://gateway.pinata.cloud/ipfs/${token.imageCid}`}
          className="big-img"
          alt={token.ticker}
        />
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>${token.ticker}</h1>
          {/* CC-027: migration badge. CC-040: non-interactive here —
              graduated tokens get explicit STON.fi / OKX buttons below,
              so the header badge is a plain status label. */}
          <div style={{ marginTop: 4 }}>
            <DexBadge
              dexStatus={token.dexStatus}
              isGraduated={token.isGraduated}
              masterAddr={token.masterAddr}
              interactive={false}
            />
          </div>
          {/* CC-027 followup: the old "graduated" pill was removed here —
              the ✅ DexBadge above is the single graduation indicator. */}
          <div className="muted">
            {tonReserves} / {targetTon} TON
          </div>
          <div className="progress" style={{ marginTop: 8, width: 180 }}>
            <div className="progress-bar" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* CC-052-E — cohort-2 legacy token: its on-chain curve predates the
          cohort-4 get-methods, so Blinkr can't trade it. View-only banner,
          no Buy/Sell card. */}
      {isLegacy && (
        <div
          className="card"
          style={{ textAlign: 'center', fontSize: 13, lineHeight: 1.5 }}
        >
          ⚠️ Токен на legacy-контракте. Только просмотр.
        </div>
      )}

      {/* CC-027 followup: a token that failed to migrate has no curve to
          trade on and no DEX pool — hide the Buy/Sell UI, show a notice. */}
      {!isLegacy && token.dexStatus === 'migration_failed' && (
        <div
          className="muted"
          style={{ textAlign: 'center', fontSize: 13, padding: '12px 0' }}
        >
          Этот токен не смог мигрировать на STON.fi. Покупка и продажа недоступны.
        </div>
      )}

      {/* CC-040 — a graduated token trades on a DEX, not the bonding
          curve. Offer both Blinkr-referral destinations as explicit
          buttons (the header badge is a plain status label). */}
      {!isLegacy && curveState?.graduated && (
        <div className="card vgap-8">
          <div className="muted" style={{ fontSize: 13 }}>
            Токен мигрировал на DEX — торгуй через STON.fi или OKX.
          </div>
          <button onClick={() => openDexLink(stonfiSwapUrl(token.masterAddr))}>
            Торговать на STON.fi
          </button>
          <button onClick={() => openDexLink(okxTokenUrl(token.masterAddr))}>
            Торговать на OKX
          </button>
        </div>
      )}

      {!isLegacy && token.dexStatus !== 'migration_failed' && !curveState?.graduated && (
        <div className="card vgap-12">
          <div className="row hgap-8">
            <button
              className={mode === 'buy' ? '' : 'secondary'}
              onClick={() => setMode('buy')}
              style={{ flex: 1 }}
            >
              Купить
            </button>
            {/* CC-037: the Sell tab shows only while the token is live on
                the bonding curve (dexStatus 'idle' && !isGraduated). Once
                it graduates / migrates, selling moves to STON.fi. */}
            {sellable && (
              <button
                className={mode === 'sell' ? '' : 'secondary'}
                onClick={() => setMode('sell')}
                style={{ flex: 1 }}
              >
                Продать
              </button>
            )}
          </div>

          {mode === 'buy' && (
            <>
              <input
                placeholder="0.10 TON"
                inputMode="decimal"
                value={input}
                onChange={(e) => setInput(e.target.value.replace(',', '.'))}
                disabled={isWorking}
              />

              <MaxFillRow
                remainingNano={remainingNano}
                onPick={(walletSendNano) => setInput(formatTon(walletSendNano, 6))}
                disabled={isWorking}
              />

              {/*
                Honest receipt — describes the on-chain behaviour rather
                than predicting numbers off-chain. The contract clamps +
                refunds excess automatically, so the user can type any
                amount and the wallet preview will show them the actual
                post-clamp jetton/refund split before they sign.
              */}
              <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                Кривая примет ещё <strong>{formatTon(remainingNano)} TON</strong>
                {' '}до graduation. Излишек автоматически возвращается. Точные
                суммы TON и jetton покажет кошелёк перед подписанием.
              </div>

              {inputError === 'zero' && (
                <div className="error-text" style={{ fontSize: 13 }}>
                  Сумма должна быть больше нуля.
                </div>
              )}
              {inputError === 'invalid' && (
                <div className="error-text" style={{ fontSize: 13 }}>
                  Введи корректную сумму в TON (цифры и необязательная дробная часть).
                </div>
              )}
              {inputError === 'tooLarge' && (
                <div className="error-text" style={{ fontSize: 13 }}>
                  Сумма превышает лимит безопасности 1000 TON.
                </div>
              )}
            </>
          )}

          {mode === 'sell' && (
            <>
              <input
                placeholder="Количество токенов"
                inputMode="decimal"
                value={sellInput}
                onChange={(e) => setSellInput(e.target.value.replace(',', '.'))}
                disabled={isWorking}
              />

              {/* CC-037 — quote preview: constant-product estimate (minus
                  1% fee) of the TON payout. Indicative only — the wallet
                  preview shows the exact amount before signing. */}
              {sellQuoteNano !== null && sellQuoteNano > 0n && (
                <div className="muted" style={{ fontSize: 13 }}>
                  Получишь примерно: <strong>{formatTon(sellQuoteNano)} TON</strong>
                </div>
              )}
              <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                Оценка по текущей цене bonding curve, комиссия 1%. Точную
                сумму к получению покажет кошелёк перед подписанием.
              </div>

              {sellInputError === 'zero' && (
                <div className="error-text" style={{ fontSize: 13 }}>
                  Количество должно быть больше нуля.
                </div>
              )}
              {sellInputError === 'invalid' && (
                <div className="error-text" style={{ fontSize: 13 }}>
                  Введи корректное число токенов.
                </div>
              )}
              {jettonWalletError && (
                <div className="error-text" style={{ fontSize: 13 }}>
                  Не удалось определить адрес твоего jetton-кошелька. Обнови страницу.
                </div>
              )}
            </>
          )}

          <button disabled={buttonDisabled} onClick={onConfirm}>
            {isWorking ? <span className="spinner" /> : buttonLabel}
          </button>

          {wallet.state.kind === 'not_ready' && (
            <div className="muted" style={{ fontSize: 12 }}>Подключение кошелька…</div>
          )}
          {wallet.state.kind === 'no_wallet' && (
            <div className="error-text">Кошелёк ещё не готов. Подожди…</div>
          )}
          {!me?.user.tonAddress && wallet.state.kind === 'ready' && (
            <div className="muted" style={{ fontSize: 12 }}>Синхронизация кошелька…</div>
          )}
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

function MaxFillRow({
  remainingNano,
  onPick,
  disabled,
}: {
  remainingNano: bigint;
  onPick: (walletSendNano: bigint) => void;
  disabled: boolean;
}) {
  // CC-032: a single "MAX" button replaces the legacy 0.1 / 1.0 TON
  // presets. Those fixed amounts made sense when targetTon was 1 TON
  // (cohort-1 dev factory) but are meaningless at cohort-2's 200 TON.
  // MAX fills the remaining-to-graduation amount plus a +5% gas/fee
  // buffer (CC-052-E: bumped from +3% for the cohort-4 3% fee; see
  // `maxBuyNano`); the contract clamps + refunds any excess, so
  // over-filling is safe.
  const maxNano = maxBuyNano(remainingNano);
  return (
    <div className="vgap-8">
      <div className="muted" style={{ fontSize: 11 }}>
        Заполнит остаток до graduation +5% на газ
      </div>
      <div className="row hgap-8" style={{ flexWrap: 'wrap', gap: 6 }}>
        <button
          className="secondary"
          onClick={() => onPick(maxNano)}
          disabled={disabled}
          style={{ padding: '6px 10px', fontSize: 13 }}
        >
          MAX
        </button>
      </div>
    </div>
  );
}
